import { initUPMFromCRM, UPMInstance } from '@upm/upm-react';
import { genLocalRouterMap } from './utils';
import { setGlobalAuthStore, ResourceType } from './hoc/auth';
import * as UpmReact from '@upm/upm-react';

import type { Router } from '@upm/upm-sdk/types/model/Menu';
import type { UpmPluginOptions } from './types';
import type { PluginFunction, BaseTreeItem } from '@kastjs/runtime-core';

interface MixRouter extends Omit<Router, 'rpcExtend'> {
  rpcExtend?: {
    lcdp?: {
      appId: number;
    };
  };
}

/**
 * 从 upm 上拉取菜单并且实例化 @upm/upm-react
 * @param options 插件参数
 */
async function getUpmResources(options: UpmPluginOptions) {
  const { instance, enableUpmMenu } = options;
  let upmInstance: UPMInstance = { permission: [], routers: [], bandGroupIds: [] };

  try {
    upmInstance = await initUPMFromCRM(options.upmParams, {
      ...(instance || {}),
      /** 如果不启用菜单合并功能，则不发送请求菜单的功能 */
      routers: instance?.routers || (enableUpmMenu ? undefined : []),
    });
  } catch (e) {
    console.log(e);
    options?.onError?.(e);
  }

  setGlobalAuthStore({
    menuAuth:
      upmInstance?.permission
        ?.filter(item => item.resourceType === ResourceType.MENU)
        .map(item => item.resourceName) || [],
    funcAuth:
      upmInstance?.permission
        ?.filter(item => item.resourceType === ResourceType.FUNCTION)
        .map(item => item.resourceName) || [],
  });

  return upmInstance;
}

/**
 * 本地菜单和远程菜单进行合并
 * @param localMenus 本地菜单
 * @param upmRouter upm上菜单
 * @param options 插件参数
 * @returns 处理好的菜单
 */
function resloveMenu(
  localMenus: BaseTreeItem[],
  upmRouter: MixRouter[],
  options: UpmPluginOptions
) {
  const { globalNoAuthRender, isMobile = false, enableUpmMenu } = options;

  let menus = [...localMenus];

  // 如果需要与upm菜单合并
  if (enableUpmMenu) {
    const localRouterMap = genLocalRouterMap(localMenus || []);
    menus = (function upmMenuRecursion(routers: MixRouter[]) {
      return routers.map(router => {
        const currentLocalRouter = localRouterMap.get(router.path);

        return {
          ...currentLocalRouter,
          ...router,
          ...router.extend,
          /** rpcExtend 兼容逻辑 */
          ...(router?.rpcExtend?.lcdp?.appId
            ? { lcdp: { appId: router.rpcExtend.lcdp.appId } }
            : {}),
          rpcExtend: router?.rpcExtend,
          /** 菜单名称 */
          name:
            currentLocalRouter?.name && typeof currentLocalRouter?.name !== 'string'
              ? currentLocalRouter?.name
              : router.meta.name,
          /**
           *  1. 如果有权限, 则展示本地菜单存在的 component
           *  2. 没有权限，则展示没有权限的页面
           */
          component: router.hasAuth ? currentLocalRouter?.component : globalNoAuthRender?.(),
          /** 是否在菜单中展示 */
          notOnMenu: router.menuType === undefined || !router.hasAuth,
          children: router.children ? upmMenuRecursion(router.children) : undefined,
        };
      });
    })(upmRouter);
  }

  return menus.filter((item: Router) => {
    try {
      let isMobileMenu = !!item.meta?.name?.includes('移动端');
      // pc和移动分别过滤菜单
      if (!!isMobile) return isMobileMenu;
      return !isMobileMenu;
    } catch (e) {
      console.error(e);
    }
    return true;
  });
}

const PluginUPM: PluginFunction<UpmPluginOptions> = async (context, options) => {
  const appSettings = context.appSettings || {};

  // 调用 @upm/upm-react 插件实例化插件
  const instance = await getUpmResources(options);
  options?.onInstanceGet?.({ ...instance });

  // 处理菜单
  const routes = resloveMenu(appSettings?.routes, instance.routers, options);
  appSettings.routes = routes;

  context.appSettings = appSettings;
};

export default PluginUPM;

export * from '@upm/upm-react';
export * from './hoc/auth';
export { UpmReact, genLocalRouterMap, ResourceType };

export type { UpmPluginOptions, UPMInstance };
